<template>
  <div class="oc-step-branding">
    <div v-if="!embedded" class="oc-step-header oc-step-header--compact mb-50">
      <div class="oc-step-header__icon oc-step-header__icon--compact">
        <feather-icon icon="ImageIcon" size="16" />
      </div>
      <div class="oc-step-header__body flex-grow-1">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div>
            <span class="oc-step-header__title">Logo &amp; design</span>
            <span class="oc-step-header__hint">
              · Sattva defaults pre-filled — change only if needed
            </span>
          </div>
          <b-badge variant="light-primary" class="oc-branding-count-badge">
            {{ uploadedImageCount }} / {{ imageKeys.length }} images
          </b-badge>
        </div>
      </div>
    </div>
    <div v-else class="oc-subsection-head mb-50">
      <feather-icon icon="ImageIcon" size="14" class="mr-50" />
      Logo &amp; design
      <b-badge variant="light-primary" class="oc-branding-count-badge ml-50">
        {{ uploadedImageCount }} / {{ imageKeys.length }}
      </b-badge>
    </div>

    <div class="oc-form-section mb-50">
      <div class="oc-form-section__head mb-75">
        <feather-icon icon="TypeIcon" size="15" class="mr-50" />
        Header &amp; footer typography
      </div>
      <p class="text-muted small mb-50">
        Stamp placement is in <strong>Write Letter</strong> settings
        (<feather-icon icon="SettingsIcon" size="12" class="align-text-top" />).
      </p>
      <b-row class="align-items-end">
        <b-col cols="6" md="3">
          <b-form-group label="Company name font (px)" label-for="oc-header-font" class="mb-50 mb-md-0">
            <b-form-input
              id="oc-header-font"
              type="number"
              min="6"
              max="24"
              size="sm"
              :value="sectionLayoutResolved('header').fontSizePx"
              :disabled="editorDisabled"
              @input="$emit('section-layout-change', 'header', 'fontSizePx', Number($event))"
            />
          </b-form-group>
        </b-col>
        <b-col cols="6" md="3">
          <b-form-group label="Logo width (px)" label-for="oc-header-logo-width" class="mb-50 mb-md-0">
            <b-form-input
              id="oc-header-logo-width"
              type="number"
              min="40"
              max="240"
              size="sm"
              :value="sectionLayoutResolved('header').logoWidthPx"
              :disabled="editorDisabled"
              @input="$emit('section-layout-change', 'header', 'logoWidthPx', Number($event))"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-checkbox
            id="oc-header-show-company"
            :checked="sectionLayoutResolved('header').showCompanyName !== false"
            switch
            class="mb-0"
            :disabled="editorDisabled"
            @change="$emit('section-layout-change', 'header', 'showCompanyName', $event)"
          >
            Show company name under logo
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-row class="mt-50">
        <b-col cols="6" md="3">
          <b-form-group label="Footer font (px)" label-for="oc-footer-font" class="mb-0">
            <b-form-input
              id="oc-footer-font"
              type="number"
              min="6"
              max="24"
              size="sm"
              :value="sectionLayoutResolved('footer').fontSizePx"
              :disabled="editorDisabled"
              @input="$emit('section-layout-change', 'footer', 'fontSizePx', Number($event))"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <b-row>
      <b-col
        v-for="imgKey in imageKeys"
        :key="imgKey"
        cols="12"
        lg="6"
        class="mb-50"
      >
        <div
          class="oc-image-card h-100"
          :class="{ 'oc-image-card--loaded': form.images[imgKey] }"
        >
          <div class="d-flex align-items-center justify-content-between mb-50">
            <div class="d-flex align-items-center">
              <div class="oc-image-card__icon mr-50">
                <feather-icon :icon="imageIcons[imgKey]" size="16" />
              </div>
              <div>
                <strong class="oc-image-card__label">{{ imageLabels[imgKey] }}</strong>
                <small class="text-muted d-block">{{ imageHints[imgKey] }}</small>
              </div>
            </div>
            <b-badge
              :variant="form.images[imgKey] ? 'light-success' : 'light-secondary'"
              class="oc-branding-badge flex-shrink-0 ml-50"
            >
              {{ form.images[imgKey] ? 'Loaded' : 'Optional' }}
            </b-badge>
          </div>

          <div v-if="form.images[imgKey]" class="oc-image-preview mb-75">
            <img
              :src="form.images[imgKey]"
              :alt="imageLabels[imgKey]"
              class="offer-configure-thumb"
            >
          </div>
          <div v-else class="oc-image-placeholder mb-75">
            <feather-icon :icon="imageIcons[imgKey]" size="28" class="text-muted mb-50" />
            <span>No {{ imageLabels[imgKey].toLowerCase() }} yet</span>
          </div>

          <b-form-input
            :id="`oc-img-${imgKey}`"
            v-model="form.images[imgKey]"
            size="sm"
            :disabled="!canManage"
            placeholder="Paste image URL…"
            class="mb-50"
          />
          <b-form-file
            v-if="canManage"
            size="sm"
            accept="image/*"
            :placeholder="`Upload ${imageLabels[imgKey]}`"
            browse-text="Browse"
            @change="$emit('image-upload', $event, imgKey)"
          />
        </div>
      </b-col>
    </b-row>

    <p class="text-muted small mb-0 mt-50 d-flex align-items-start">
      <feather-icon icon="InfoIcon" size="13" class="mr-50 mt-25 flex-shrink-0" />
      Keep each image under ~200 KB; total template must stay under 2 MB.
    </p>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BBadge,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormFile,
} from 'bootstrap-vue'

export default {
  name: 'OfferConfigureStepBranding',
  components: {
    BRow,
    BCol,
    BBadge,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormFile,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    canManage: {
      type: Boolean,
      default: false,
    },
    editorDisabled: {
      type: Boolean,
      default: false,
    },
    uploadedImageCount: {
      type: Number,
      default: 0,
    },
    imageKeys: {
      type: Array,
      required: true,
    },
    imageLabels: {
      type: Object,
      required: true,
    },
    imageHints: {
      type: Object,
      required: true,
    },
    imageIcons: {
      type: Object,
      required: true,
    },
    sectionLayoutResolved: {
      type: Function,
      required: true,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style scoped>
.oc-form-section {
  background: #f8f9fb;
  border: 1px solid #eef0f3;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
}

.oc-form-section__head {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #2c2c2c;
}

.oc-branding-count-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
}

.oc-subsection-head {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #2c2c2c;
}

.oc-image-card {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.oc-image-card--loaded {
  border-color: rgba(0, 186, 176, 0.35);
  box-shadow: 0 2px 6px rgba(0, 186, 176, 0.08);
}

.oc-image-card__icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.4rem;
  background: rgba(0, 186, 176, 0.12);
  color: #00bab0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.oc-image-card__label {
  font-size: 0.875rem;
}

.oc-branding-badge {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.oc-image-preview {
  background: #f8f9fb;
  border: 1px solid #eef0f3;
  border-radius: 0.4rem;
  padding: 0.75rem;
  text-align: center;
}

.oc-image-placeholder {
  background: #f8f9fb;
  border: 2px dashed #d8d6de;
  border-radius: 0.4rem;
  padding: 1.25rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6e6b7b;
  font-size: 0.8125rem;
}
</style>
